import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Calendar, User, Download, Eye } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SummariesPage = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSummaries();
  }, []);

  const fetchSummaries = async () => {
    try {
      const response = await axios.get(`${API}/cases`);
      // Filter only completed cases
      const completedCases = response.data.filter(c => c.status === 'completed');
      
      // Show all completed cases - the case detail page will handle empty results gracefully
      setSummaries(completedCases);
      console.log(`[DEBUG] Found ${completedCases.length} completed cases`);
    } catch (error) {
      console.error("Error fetching summaries:", error);
      toast.error("Failed to load summaries");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (caseId, format) => {
    try {
      const mimeTypes = {
        'pdf': 'application/pdf',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      };
      
      const response = await axios.get(`${API}/cases/${caseId}/download/${format}`, {
        responseType: 'blob',
        headers: {
          'Accept': mimeTypes[format] || 'application/octet-stream'
        }
      });
      
      // Create blob with explicit type
      const blob = new Blob([response.data], { type: mimeTypes[format] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `chronology_${caseId}.${format}`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      toast.error(`Failed to download ${format.toUpperCase()}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
            View Summaries
          </h1>
          <p className="text-lg" style={{ color: '#F5F7FA' }}>
            All completed medical chronologies and billing summaries
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#245451' }}></div>
            <p className="mt-4" style={{ color: '#F5F7FA' }}>Loading summaries...</p>
          </div>
        ) : summaries.length === 0 ? (
          <Card className="glass-card border-dashed border-2">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 mb-4" style={{ color: '#84a2ac' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#1A1A1A' }}>No summaries yet</h3>
              <p className="mb-6" style={{ color: '#406962' }}>Complete a case to generate your first summary</p>
              <Button 
                onClick={() => navigate('/upload')}
                style={{ backgroundColor: '#245451' }}
              >
                Create New Case
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {summaries.map((summary) => (
              <Card 
                key={summary.id}
                className="glass-card hover:shadow-lg transition-all"
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2" style={{ color: '#245451' }}>
                        {summary.case_name}
                      </CardTitle>
                      <CardDescription className="space-y-1" style={{ color: '#64748B' }}>
                        <div className="flex items-center text-sm">
                          <User className="h-3.5 w-3.5 mr-1.5" />
                          {summary.patient_name}
                        </div>
                        <div className="flex items-center text-sm">
                          <Calendar className="h-3.5 w-3.5 mr-1.5" />
                          {new Date(summary.date_of_injury).toLocaleDateString()}
                        </div>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button
                      onClick={() => navigate(`/case/${summary.id}`)}
                      className="w-full"
                      style={{ backgroundColor: '#245451' }}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      View Summary
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleDownload(summary.id, 'pdf')}
                        variant="outline"
                        className="flex-1"
                        style={{ borderColor: '#E2E8F0', color: '#245451' }}
                      >
                        <Download className="mr-1 h-3 w-3" />
                        PDF
                      </Button>
                      <Button
                        onClick={() => handleDownload(summary.id, 'docx')}
                        variant="outline"
                        className="flex-1"
                        style={{ borderColor: '#E2E8F0', color: '#245451' }}
                      >
                        <Download className="mr-1 h-3 w-3" />
                        Word
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs mt-3" style={{ color: '#84a2ac' }}>
                    Completed {new Date(summary.created_at).toLocaleDateString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SummariesPage;